import { useCallback } from 'react';
import {
  useLocation,
  useNavigate,
  useSearchParams,
  type NavigateOptions,
} from 'react-router-dom';
import { useIframeModeStore } from '@/hooks/useIframeModeStore';
import { SMP_PATH } from '@/constants/paths';

/**
 * SMP navigation helpers that preserve the selected `?subscriptionId=` across
 * in-SMP routes. The subscription selection is canonical in the URL, so every
 * in-SMP link/navigate must carry it forward — otherwise a sub-route re-resolves
 * without the id and mis-scopes (or bounces to the selector).
 *
 * - `withSubId(path)` — for `<Link to=…>`: appends the current subscriptionId.
 * - `smpNavigate(path, options?)` — for imperative navigation.
 * - `smpGoBack(fallbackPath?)` — for "Back"/post-mutation returns.
 */
export const useSMPNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const iframeModeState = useIframeModeStore();
  // URL query first; fall back to the iframe-mode store (mobile webview passes
  // subscriptionId as an in_iframe param that may not survive client-side nav) —
  // mirrors the resolution in useSMPData so in-SMP links don't drop the id.
  const subscriptionId =
    searchParams.get('subscriptionId') ||
    (iframeModeState.subscriptionId
      ? String(iframeModeState.subscriptionId)
      : null);

  const withSubId = useCallback(
    (path: string) => {
      if (!subscriptionId) return path;
      const separator = path.includes('?') ? '&' : '?';
      return `${path}${separator}subscriptionId=${subscriptionId}`;
    },
    [subscriptionId],
  );

  const smpNavigate = useCallback(
    (path: string, options?: NavigateOptions) => navigate(withSubId(path), options),
    [navigate, withSubId],
  );

  const smpGoBack = useCallback(
    (fallbackPath: string = SMP_PATH) => {
      if (location.key !== 'default') {
        navigate(-1);
        return;
      }
      navigate(withSubId(fallbackPath), { replace: true });
    },
    [navigate, withSubId, location.key],
  );

  return { withSubId, smpNavigate, smpGoBack };
};
